import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Wallet, Coins, Zap, LifeBuoy, Users, ShoppingBag,
  Ban, ShieldCheck, Trash2, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const brl = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
const dt = (s) => (s ? String(s).slice(0, 19).replace("T", " ") : "—");

const STATUS_TONE = {
  approved: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30",
  pending: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
  incorrect: "bg-destructive/15 text-destructive border-destructive/30",
};
const statusBadge = (s) => (
  <Badge variant="outline" className={STATUS_TONE[s] || "bg-muted text-muted-foreground"}>{s}</Badge>
);

function Kpi({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" /><span className="text-xs">{label}</span>
      </div>
      <div className="mt-2 font-num text-2xl font-bold">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function AdminUserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [credit, setCredit] = useState("");
  const [balance, setBalance] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/users/${id}/profile`).then(({ data }) => setData(data)).catch((e) => { toast.error(apiError(e)); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-muted-foreground">Carregando...</div>;
  const { user: u, stats: s, parent } = data;

  const patch = async (payload, ok) => {
    try { await api.patch(`/admin/users/${id}`, payload); toast.success(ok); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const adjustCredit = async () => {
    const amt = Number(credit); if (!amt) return toast.error("Valor inválido");
    try { await api.post("/credits/adjust", { user_id: id, amount: amt, description: "Ajuste manual (admin)" }); toast.success("Créditos ajustados"); setCredit(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const adjustBalance = async () => {
    const amt = Number(balance); if (!amt) return toast.error("Valor inválido");
    try { await api.post(`/admin/users/${id}/balance`, { amount: amt }); toast.success("Saldo ajustado"); setBalance(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Excluir a conta de ${u.name}? Esta ação é permanente.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Conta excluída"); navigate("/admin/usuarios"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-user-profile">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/usuarios")} data-testid="profile-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl" data-testid="profile-name">{u.name}</h2>
              <Badge variant="secondary" className="capitalize">{u.role}</Badge>
              {u.telegram_id && <Badge variant="outline" className="text-[10px]">🔗 TG</Badge>}
              {u.store_locked && <Badge variant="outline" className="text-[10px]">🔒 Loja</Badge>}
              {u.blocked ? <Badge variant="destructive">Bloqueado</Badge> : <Badge variant="secondary">Ativo</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">{u.email}{u.whatsapp ? ` · ${u.whatsapp}` : ""}</p>
          </div>
        </div>
        {u.telegram_id && (
          <a href={`https://t.me/${u.telegram_username || ""}`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="gap-2" data-testid="profile-tg-link"><Send className="h-4 w-4" /> {u.telegram_username ? `@${u.telegram_username}` : `ID ${u.telegram_id}`}</Button>
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Wallet} label="Saldo" value={brl(u.balance)} testid="profile-kpi-balance" />
        <Kpi icon={Coins} label="Créditos" value={Number(u.credits || 0)} testid="profile-kpi-credits" />
        <Kpi icon={Zap} label="Ativações" value={s.activations_total} hint={`${s.activations_approved} aprovadas · ${s.activations_failed} c/ erro`} testid="profile-kpi-activations" />
        <Kpi icon={LifeBuoy} label="Tickets abertos" value={s.tickets_open} testid="profile-kpi-tickets" />
        <Kpi icon={ShoppingBag} label="Gasto total" value={brl(s.spent_total)} hint={`${s.orders_paid} pedidos pagos`} testid="profile-kpi-spent" />
        <Kpi icon={Users} label="Indicados" value={s.referrals} testid="profile-kpi-referrals" />
        <Kpi icon={Zap} label="Pendentes" value={s.activations_pending} testid="profile-kpi-pending" />
        <Kpi icon={Users} label="Revendedor pai" value={parent ? parent.name : "—"} hint={parent?.email} testid="profile-kpi-parent" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Quick actions */}
        <div className="space-y-4 rounded-xl border border-border bg-card p-5 lg:col-span-1" data-testid="profile-quick-actions">
          <h3 className="font-heading font-semibold">Ações rápidas</h3>

          <div className="space-y-1.5">
            <Label>Créditos (atual: {Number(u.credits || 0)})</Label>
            <div className="flex gap-2">
              <Input type="number" step="0.1" value={credit} onChange={(e) => setCredit(e.target.value)} placeholder="+ / −" data-testid="profile-credit-input" />
              <Button onClick={adjustCredit} data-testid="profile-credit-btn">OK</Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Saldo R$ (atual: {brl(u.balance)})</Label>
            <div className="flex gap-2">
              <Input type="number" step="0.01" value={balance} onChange={(e) => setBalance(e.target.value)} placeholder="+ / −" data-testid="profile-balance-input" />
              <Button onClick={adjustBalance} data-testid="profile-balance-btn">OK</Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Papel</Label>
            <Select value={u.role} onValueChange={(v) => patch({ role: v }, "Papel atualizado")}>
              <SelectTrigger data-testid="profile-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Usuário</SelectItem>
                <SelectItem value="reseller">Revendedor</SelectItem>
                <SelectItem value="support">Atendente</SelectItem>
                <SelectItem value="admin">Administrador</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
            <span>Travar na loja</span>
            <Switch checked={!!u.store_locked} onCheckedChange={(v) => patch({ store_locked: v }, v ? "Travado na loja" : "Liberado")} data-testid="profile-lock-switch" />
          </label>

          <div className="flex gap-2 pt-1">
            <Button variant={u.blocked ? "outline" : "secondary"} className="flex-1 gap-2" onClick={() => patch({ blocked: !u.blocked }, u.blocked ? "Desbloqueado" : "Bloqueado")} data-testid="profile-block-btn">
              {u.blocked ? <><ShieldCheck className="h-4 w-4" /> Desbloquear</> : <><Ban className="h-4 w-4" /> Bloquear</>}
            </Button>
            <Button variant="destructive" size="icon" onClick={del} data-testid="profile-delete-btn"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* Activity */}
        <div className="space-y-6 lg:col-span-2">
          <Section title="Ativações recentes" testid="profile-activations">
            {data.activations.length === 0 ? <Empty /> : data.activations.map((a, i) => (
              <Row key={i} left={a.app_name || "—"} sub={`${a.mac || a.device_id || ""} · ${dt(a.created_at)}`} right={statusBadge(a.status)} />
            ))}
          </Section>

          <Section title="Tickets de suporte" testid="profile-tickets">
            {data.tickets.length === 0 ? <Empty /> : data.tickets.map((t) => (
              <Link key={t.id} to={`/admin/tickets/${t.id}`} className="block rounded-lg px-1 transition-colors hover:bg-muted">
                <Row left={t.subject || "Chamado"} sub={dt(t.created_at)} right={<Badge variant="outline" className="capitalize">{t.status}</Badge>} />
              </Link>
            ))}
          </Section>

          <Section title="Pedidos / pagamentos" testid="profile-orders">
            {data.orders.length === 0 ? <Empty /> : data.orders.map((o, i) => (
              <Row key={i} left={o.app_name || o.description || o.kind} sub={`${o.source || "web"} · ${dt(o.created_at)}`} right={<div className="text-right"><div className="font-num">{brl((o.amount_cents || 0) / 100)}</div><div className="text-xs text-muted-foreground">{o.status}</div></div>} />
            ))}
          </Section>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Section title="Extrato de créditos" testid="profile-credit-ledger">
              {data.credit_ledger.length === 0 ? <Empty /> : data.credit_ledger.map((l, i) => (
                <Row key={i} left={l.description || l.type} sub={dt(l.created_at)} right={<span className={`font-num ${l.amount >= 0 ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>{l.amount >= 0 ? "+" : ""}{l.amount}</span>} />
              ))}
            </Section>
            <Section title="Extrato de saldo" testid="profile-balance-ledger">
              {data.balance_ledger.length === 0 ? <Empty /> : data.balance_ledger.map((l, i) => (
                <Row key={i} left={l.description || l.type} sub={dt(l.created_at)} right={<span className={`font-num ${l.amount >= 0 ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>{l.amount >= 0 ? "+" : ""}{brl(l.amount)}</span>} />
              ))}
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <h3 className="mb-2 font-heading font-semibold">{title}</h3>
      <div className="divide-y divide-border/60">{children}</div>
    </div>
  );
}
function Row({ left, sub, right }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{left}</div>
        {sub && <div className="truncate font-mono text-xs text-muted-foreground">{sub}</div>}
      </div>
      <div className="shrink-0">{right}</div>
    </div>
  );
}
function Empty() {
  return <p className="py-3 text-sm text-muted-foreground">Nada por aqui ainda.</p>;
}
